import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Calculator,
  ReceiptText,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  CreditCard,
  Loader2,
} from "lucide-react";
import DashboardContainer from "../layout/DashboardContainer";
import { ClientInvoice, RejectInvoiceRequest } from "@/types/client";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { clientService } from "@/services/clientService";

// Payment link interface
interface PaymentLink {
  payment_url: string;
  amount: number;
  currency: string;
  payment_methods: string[];
  status: string;
}

const LineItemDisplay: React.FC<{
  item: ClientInvoice["lineItems"][0];
  index: number;
}> = ({ item, index }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Line Item {index + 1}</h4>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Product/Service
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              {item.product}
            </div>
          </div>

          {item.description && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Description
              </label>
              <div className="p-3 bg-muted rounded-md text-sm">
                {item.description}
              </div>
            </div>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Unit Price
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              {formatCurrency(parseFloat(item.rate.toString()) || 0)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Quantity / Hours
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              {item.quantity}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Tax
            </label>
            <div className="p-3 bg-muted rounded-md text-sm">
              {formatCurrency(parseFloat(item.tax.toString()) || 0)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Line Total
            </label>
            <div className="p-3 bg-muted rounded-md text-sm font-medium">
              {formatCurrency(parseFloat(item.total.toString()) || 0)}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export function ClientViewInvoiceDetails({
  invoice,
  onBack,
  onInvoiceAction,
}: {
  invoice: ClientInvoice;
  onBack: () => void;
  onInvoiceAction: () => void;
}) {
  const { toast } = useToast();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Payment link state
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "APPROVED":
        return "default";
      case "SUBMITTED":
        return "secondary";
      case "DRAFT":
        return "outline";
      case "REJECTED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Load payment link when invoice is approved
  useEffect(() => {
    if (invoice.status === 'APPROVED' && invoice.id) {
      loadPaymentLink();
    }
  }, [invoice.status, invoice.id]);

  const loadPaymentLink = async () => {
    if (!invoice.id) return;
    
    try {
      setLoadingPayment(true);
      const response = await clientService.getInvoicePaymentLink(invoice.id);
      setPaymentLink(response);
    } catch (error) {
      console.error('Failed to load payment link:', error);
      // Payment link might not be available if contractor hasn't connected Stripe
    } finally {
      setLoadingPayment(false);
    }
  };

  const handlePayNow = () => {
    if (paymentLink?.payment_url) {
      window.open(paymentLink.payment_url, '_blank');
    }
  };

  const handleApproveInvoice = async () => {
    try {
      setIsProcessing(true);

      await ApiService.request(EndpointKeys.APPROVE_CLIENT_INVOICE, {
        method: "POST",
        urlParams: { invoiceId: invoice.id },
      });

      toast({
        title: "Success",
        description: "Invoice approved successfully",
      });

      onInvoiceAction();
    } catch (error) {
      console.error("Error approving invoice:", error);
      toast({
        title: "Error",
        description: "Failed to approve invoice",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectInvoice = async () => {
    try {
      setIsProcessing(true);

      const rejectData: RejectInvoiceRequest = {
        reason: rejectReason.trim() || undefined,
      };

      await ApiService.request(EndpointKeys.REJECT_CLIENT_INVOICE, {
        method: "POST",
        urlParams: { invoiceId: invoice.id },
        body: rejectData,
      });

      toast({
        title: "Success",
        description: "Invoice rejected successfully",
      });

      setShowRejectModal(false);
      setRejectReason("");
      onInvoiceAction();
    } catch (error) {
      console.error("Error rejecting invoice:", error);
      toast({
        title: "Error",
        description: "Failed to reject invoice",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const openRejectModal = () => {
    setRejectReason("");
    setShowRejectModal(true);
  };

  const headerContent = (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button variant="outline" onClick={onBack} className="w-full sm:w-auto">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Invoices
      </Button>
      {invoice.status === "SUBMITTED" && (
        <div className="flex gap-2">
          <Button
            onClick={handleApproveInvoice}
            disabled={isProcessing}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
            data-testid="approve-invoice-button"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isProcessing ? "Approving..." : "Approve"}
          </Button>
          <Button
            variant="destructive"
            onClick={openRejectModal}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <DashboardContainer
        title={`Invoice ${invoice.invoiceNumber || `#${invoice.id}`}`}
        headerContent={headerContent}
      >
        <div className="space-y-6">
          {/* Invoice Status & Overview */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <ReceiptText className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      {invoice.invoiceNumber || `Invoice #${invoice.id}`}
                    </CardTitle>
                    <p className="text-muted-foreground">
                      Created on {formatDate(invoice.createdAt)}
                    </p>
                  </div>
                </div>
                <Badge variant={getStatusBadgeVariant(invoice.status)}>
                  {formatStatus(invoice.status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Due Date</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Line Items</p>
                    <p className="text-sm text-muted-foreground">
                      {invoice.lineItems.length} item
                      {invoice.lineItems.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calculator className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Total Amount</p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(
                        parseFloat(invoice.grandTotal.toString()) || 0,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Invoice Number
                  </label>
                  <div className="p-3 bg-muted rounded-md text-sm">
                    {invoice.invoiceNumber || "Auto-generated"}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Due Date
                  </label>
                  <div className="p-3 bg-muted rounded-md text-sm">
                    {formatDate(invoice.dueDate)}
                  </div>
                </div>
              </div>

              {invoice.notes && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Notes
                  </label>
                  <div className="p-3 bg-muted rounded-md text-sm">
                    {invoice.notes}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Line Items</h3>

            <div className="space-y-4">
              {invoice.lineItems.map((item, index) => (
                <LineItemDisplay key={item.id} item={item} index={index} />
              ))}
            </div>
          </div>

          {/* Invoice Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Invoice Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span>
                    {formatCurrency(
                      parseFloat(invoice.subtotal.toString()) || 0,
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Tax:</span>
                  <span>
                    {formatCurrency(
                      parseFloat(invoice.totalTax.toString()) || 0,
                    )}
                  </span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-medium">
                    <span>Total:</span>
                    <span>
                      {formatCurrency(
                        parseFloat(invoice.grandTotal.toString()) || 0,
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Options */}
          {invoice.status === 'APPROVED' && (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Payment Options</h3>
              
              {loadingPayment ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : paymentLink ? (
                <div className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      This invoice has been approved and is ready for payment.
                      You can pay securely using the link below.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Amount</p>
                      <p className="text-2xl font-bold">
                        {paymentLink.currency === 'USD' ? '$' : '£'}
                        {paymentLink.amount.toFixed(2)} {paymentLink.currency}
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handlePayNow}
                      size="lg"
                      className="min-w-[150px]"
                    >
                      <CreditCard className="mr-2 h-5 w-5" />
                      Pay Now
                    </Button>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    <p>Accepted payment methods:</p>
                    <ul className="list-disc list-inside mt-1">
                      {paymentLink.payment_methods.includes('card') && <li>Credit/Debit Card</li>}
                      {paymentLink.payment_methods.includes('us_bank_account') && <li>ACH Bank Transfer (US)</li>}
                      {paymentLink.payment_methods.includes('bacs_debit') && <li>BACS Direct Debit (UK)</li>}
                    </ul>
                  </div>
                </div>
              ) : (
                <Alert>
                  <AlertDescription>
                    Online payment is not available for this invoice. 
                    Please contact the contractor for alternative payment methods.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </DashboardContainer>

      {/* Reject Invoice Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Invoice</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this invoice. This will help
              the contractor understand what needs to be changed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reject-reason">
                Reason for Rejection (Optional)
              </Label>
              <Textarea
                id="reject-reason"
                placeholder="Please explain why you're rejecting this invoice..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={isProcessing}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectInvoice}
              disabled={isProcessing}
            >
              {isProcessing ? "Rejecting..." : "Reject Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
